import React, { useState, useRef } from 'react'
import { FiX, FiPlus, FiEye } from 'react-icons/fi'
import useImageUpload from '@/hooks/useImageUpload'
import LoadingSpinner from './LoadingSpinner'
import GigImage from './GigImage'

interface GigImageUploadProps {
    images?: string[]
    onImagesChange?: (imageIds: string[]) => void
    onUploadError?: (error: string) => void
    maxImages?: number
    disabled?: boolean
    className?: string
    onFullscreenImage?: (imageUrl: string | null) => void
}

interface ImagePreview {
    id: string
    url: string
    isUploading?: boolean
    uploadedImageId?: string
}

const GigImageUpload: React.FC<GigImageUploadProps> = ({
    images = [],
    onImagesChange,
    onUploadError,
    maxImages = 5,
    disabled = false,
    className = '',
    onFullscreenImage
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([])
    const [uploadedImageData, setUploadedImageData] = useState<{ [key: string]: any }>({})

    const {
        uploading,
        uploadProgress,
        uploadMultipleImages,
        validateFile,
    } = useImageUpload({
        onUploadSuccess: (uploadedImages) => {
            // uploadedImages can be a single image or array of images
            const imageArray = Array.isArray(uploadedImages) ? uploadedImages : [uploadedImages]
            const newImageIds = imageArray.map(img => img._id)

            // Store the uploaded image data to avoid extra API calls
            const imageDataMap: { [key: string]: any } = {}
            imageArray.forEach(img => {
                imageDataMap[img._id] = img
            })
            setUploadedImageData(prev => ({ ...prev, ...imageDataMap }))

            // Update previews: mark as done and link to uploaded image
            // Use the dataUrl from response if available (works without extra requests)
            setImagePreviews(prev => {
                const uploading = prev.filter(p => p.isUploading)
                const completed = uploading.slice(0, imageArray.length).map((p, index) => ({
                    ...p,
                    isUploading: false,
                    url: imageArray[index].dataUrl || p.url,
                    uploadedImageId: imageArray[index]._id
                }))
                // Return non-uploading previews + updated completed ones
                return [...prev.filter(p => !p.isUploading), ...completed]
            })

            // Update the parent component with all image IDs (saved to draft automatically)
            const updatedImages = [...images, ...newImageIds]
            onImagesChange?.(updatedImages)
        },
        onUploadError: (error) => {
            onUploadError?.(error)
            setImagePreviews([])
        },
        showToastMessages: true
    })

    const canAddMore = images.length + imagePreviews.length < maxImages

    const handleFileSelect = async (files: FileList) => {
        const fileArray = Array.from(files)
        const availableSlots = maxImages - images.length - imagePreviews.length
        const filesToUpload = fileArray.slice(0, availableSlots)

        // Validate all files first
        for (const file of filesToUpload) {
            const validation = validateFile(file)
            if (!validation.valid) {
                onUploadError?.(validation.error || 'Invalid file')
                return
            }
        }

        // Create previews for selected files
        const previews: ImagePreview[] = filesToUpload.map((file, index) => ({
            id: `preview-${Date.now()}-${index}`,
            url: URL.createObjectURL(file),
            isUploading: true
        }))

        setImagePreviews(prev => [...prev, ...previews])

        try {
            await uploadMultipleImages(filesToUpload, {
                imageType: 'gig',
                quality: 85,
                maxWidth: 1200,
                maxHeight: 900
            })
        } catch (error) {
            // Clean up preview URLs on error
            previews.forEach(preview => URL.revokeObjectURL(preview.url))
            setImagePreviews(prev => prev.filter(p => !previews.find(preview => preview.id === p.id)))
        }
    }

    const handleClick = () => {
        if (!disabled && canAddMore && fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files && files.length > 0) {
            handleFileSelect(files)
        }
        // Reset input value to allow selecting the same files again
        event.target.value = ''
    }

    const removeImage = (imageId: string) => {
        if (disabled || uploading) return

        // Also remove from uploaded data if it exists
        setUploadedImageData(prev => {
            const newData = { ...prev }
            delete newData[imageId]
            return newData
        })

        const updatedImages = images.filter(id => id !== imageId)
        onImagesChange?.(updatedImages)
    }

    const removePreview = (previewId: string) => {
        setImagePreviews(prev => {
            const preview = prev.find(p => p.id === previewId)
            if (preview) {
                URL.revokeObjectURL(preview.url)
            }
            return prev.filter(p => p.id !== previewId)
        })
    }

    const openFullscreen = (imageUrl: string) => {
        onFullscreenImage?.(imageUrl)
    }

    const closeFullscreen = () => {
        onFullscreenImage?.(null)
    }



    // Clean up preview URLs on unmount
    React.useEffect(() => {
        return () => {
            imagePreviews.forEach(preview => {
                URL.revokeObjectURL(preview.url)
            })
        }
    }, [])

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Current Images Grid */}
            {(images.length > 0 || imagePreviews.length > 0) && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Existing uploaded images */}
                    {images.map((imageId, index) => (
                        <div key={imageId} className="relative group">
                            <GigImage
                                imageId={imageId}
                                imageData={uploadedImageData[imageId] || uploadedImageData[imageId]}
                                alt={`Gig image ${index + 1}`}
                                size="lg"
                                className="w-full h-32"
                            />
                            {/* Action buttons */}
                            <div className="absolute top-2 right-2 flex gap-1">
                                <button
                                    type="button"
                                    onClick={async (e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        try {
                                            // First try to get from uploaded data
                                            const imageData = uploadedImageData[imageId]
                                            if (imageData?.dataUrl) {
                                                openFullscreen(imageData.dataUrl)
                                                return
                                            }

                                            // Otherwise fetch the image data
                                            const { imageService } = await import('@/utils/imageService')
                                            const fetchedImageData = await imageService.getImageById(imageId)
                                            if (fetchedImageData?.dataUrl) {
                                                openFullscreen(fetchedImageData.dataUrl)
                                            }
                                        } catch (error) {
                                            console.error('Failed to load full size image:', error)
                                        }
                                    }}
                                    className="p-1 bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-100 rounded-full hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                                    title="View full size"
                                >
                                    <FiEye className="w-4 h-4" />
                                </button>
                                {!disabled && (
                                    <button
                                        onClick={() => removeImage(imageId)}
                                        className="p-1 bg-red-500 dark:bg-red-600 text-white dark:text-gray-100 rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove image"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Preview images (while uploading or not yet in images array) */}
                    {imagePreviews.filter(preview => !images.includes(preview.uploadedImageId || '')).map((preview) => (
                        <div key={preview.id} className="relative group">
                            <div className="relative w-full h-32 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                <img
                                    src={preview.url}
                                    alt="Uploading..."
                                    className="w-full h-full object-cover"
                                />
                                {preview.isUploading && (
                                    <div className="absolute inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-60 flex flex-col items-center justify-center">
                                        <LoadingSpinner size="sm" color="white" />
                                        <div className="text-xs text-white dark:text-gray-200 mt-1">
                                            {uploadProgress}%
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Action buttons for preview */}
                            {!preview.isUploading && (
                                <div className="absolute top-2 right-2 flex gap-1">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            openFullscreen(preview.url)
                                        }}
                                        className="p-1 bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-100 rounded-full hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
                                        title="View full size"
                                    >
                                        <FiEye className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => removePreview(preview.id)}
                                        className="p-1 bg-red-500 dark:bg-red-600 text-white dark:text-gray-100 rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove preview"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Upload Button/Area */}
            {canAddMore && (
                <div
                    onClick={handleClick}
                    className={`
                        w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg
                        flex flex-col items-center justify-center cursor-pointer transition-colors
                        ${!disabled ? 'hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20' : ''}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                >
                    {uploading ? (
                        <div className="flex flex-col items-center">
                            <LoadingSpinner size="md" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                Uploading... {uploadProgress}%
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-2">
                                <FiPlus className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                            </div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Add Images
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
                                JPEG, JPG, PNG up to 10MB each
                                <br />
                                {images.length + imagePreviews.length} of {maxImages} images
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                multiple
                onChange={handleFileChange}
                className="hidden"
                disabled={disabled}
                aria-label="Upload gig images"
                title="Upload gig images"
            />

            {/* Instructions */}
            {images.length === 0 && imagePreviews.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    <p className="font-medium">Tips for better gig images:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Show examples of your work or the service you provide</li>
                        <li>Use high-quality, well-lit photos</li>
                        <li>Include before/after shots if applicable</li>
                        <li>Avoid blurry or heavily filtered images</li>
                    </ul>
                </div>
            )}
        </div>
    )
}

export default GigImageUpload
